import * as yup from "yup"

import { Flex, Form, Row } from "antd"
import {
  Button,
  Icon,
  Legend,
  Modal,
  PhoneInput,
  SelectInput,
  Spinner,
  Text,
  notify,
  notification,
  Group,
} from "components/common"
import {
  useAddLeadMutation,
  useChangeAssigneeMutation,
  useGetReasonForAllocationQuery,
  useLazyGetClientDetailQuery,
} from "features/clients/detail/api"
import { forwardRef, useEffect, useMemo, useRef, useState } from "react"

import { useForm, useWatch } from "antd/lib/form/Form"
import { IconText } from "components/custom"
import { regex } from "constants/regex"
import { AddLeadForm } from "features/leads/components"
import { useLazyGetCheckPhoneNumberQuery } from "features/shared/api"
import { schemaRules } from "features/shared/utils"
import { useQueryUpdate } from "hooks"
import _ from "lodash"
import { useSelector } from "react-redux"
import { fireEvent } from "utils/gtm/gtmEventHandler"
import { ACTION_TYPES } from "utils/gtm/gtmEventsData"
import theme from "utils/themeVars"
import { AddClientForm } from ".."
import { useTranslation } from "react-i18next"
import AffiliateSection from "../clientForm/affiliateSection"
import { affiliateOptions } from "constants/list"
import getClientLeadValidationSchema from "./clientLeadValidationSchema"
import parentApi from "store/parentApi"

const ClientLeadForm = forwardRef((props, ref) => {
  const { t } = useTranslation()
  const [initializeClientForm] = useForm()
  const addClientFormRef = useRef()
  const addLeadFormRef = useRef()
  const mobileNumber = useWatch("mobileNumber", initializeClientForm)
  const { affiliateId } = useWatch("searchAffiliate", initializeClientForm) ?? {}
  const user = useSelector(state => state.app.user?.info)
  const isAfilliateUser = useSelector(state => state.app.user?.info?.TeamType?.key) === "salesaff"
  const privileges = useSelector(state => state.app.privileges)
  const [
    checkPhone,
    {
      data: phoneData,
      isError: isPhoneError,
      error: phoneError,
      isFetching: phoneValidationFetching,
      isUninitialized: isCheckPhoneUninitialized,
    },
  ] = useLazyGetCheckPhoneNumberQuery()
  const [addLead, { isLoading: isAddLeadLoading, error, reset: resetAddLead }] = useAddLeadMutation()
  const [getClientDetail, { data: client, isFetching: isClientFetching, isUninitialized: isGetClientUninitialized }] =
    useLazyGetClientDetailQuery()
  const [
    changeAssignee,
    {
      data: changeAssigneeData,
      isLoading: isChangeAssigneeLoading,
      reset,
      isUninitialized: isChangeAssigneeUninitialized,
    },
  ] = useChangeAssigneeMutation()

  const { data: reasons, isFetching: fetchingReasons } = useGetReasonForAllocationQuery()

  const [stage, setStage] = useState(0)
  const [reason, setReason] = useState(null)
  const [errorState, setErrorState] = useState(null)
  const [clientFormValues, setClientFormValues] = useState(null)

  const [update] = useQueryUpdate()

  let rules = schemaRules(getClientLeadValidationSchema())

  const disableButton = useMemo(() => {
    const remianingConditions =
      isPhoneError ||
      phoneValidationFetching ||
      (phoneData?.exists && phoneData?.userId !== user.id) ||
      isChangeAssigneeLoading ||
      isClientFetching ||
      phoneData?.isUserCellNumber ||
      (phoneData?.isAllocationPossible && !phoneData?.isAllocationPossible) ||
      isAddLeadLoading

    return phoneData?.isAllocationPossible
      ? !!isAfilliateUser
        ? !affiliateId || !reason
        : !reason
      : remianingConditions
  }, [
    phoneData,
    isAddLeadLoading,
    phoneValidationFetching,
    isChangeAssigneeLoading,
    isClientFetching,
    reason,
    affiliateId,
  ])

  useEffect(() => {
    setStage(0)
  }, [])

  const onModalVisible = () => {
    setErrorState(null)
  }

  useEffect(() => {
    if (stage === 0) {
      setErrorState(null)
      const cell = initializeClientForm.getFieldValue("mobileNumber")
      !isCheckPhoneUninitialized &&
        checkPhone({ cell })
          .unwrap()
          .catch(error => setErrorState(error?.message))
    }
    if (stage === 1) {
      addClientFormRef.current?.setFieldsValue({
        ...(clientFormValues && clientFormValues),
        allocatedTo: {
          value: user.id,
          label: user.name,
          id: user.id.toString(),
        },
        mobileNumber: [initializeClientForm.getFieldValue("mobileNumber")],
      })
    }
  }, [stage])

  const onReallocate = async () => {
    reset()
    try {
      await changeAssignee({
        clientIds: [phoneData?.data?.Client?.id],
        type: "add-client",
        allocatedTo: { id: user.id, name: user.name },
        reasonId: reason,
        requestReAllocation: true,
        referralId: affiliateId,
        referralType: !!isAfilliateUser ? "affiliate" : "direct",
        userPhone: mobileNumber,
      }).unwrap()
      handleStageChange("backward")
      notification.success({ message: t("Client has been re-allocated successfully") })
    } catch (e) {
      notify.error(e.message)
    }
  }

  const handleStageChange = (mode = "forward") => {
    if (mode === "forward") {
      if (stage === 0) {
        initializeClientForm.submit()
      }
      if (stage === 1) {
        addClientFormRef.current?.submit()
      }
      if (stage === 2) {
        addLeadFormRef.current?.submit()
      }
    }
    if (mode === "backward") {
      if (stage > 0) {
        setStage(prevStage => (client ? prevStage - 2 : prevStage - 1))
      } else resetForm()
    }
  }

  const resetForm = () => {
    ref?.current?.setVisible(false)
    setStage(0)
    setReason(null)
    setClientFormValues(null)
    initializeClientForm.resetFields()
    addClientFormRef.current?.resetFields()
    addLeadFormRef.current?.resetFields()
    !isGetClientUninitialized && update("getClientDetail", draft => null)
    !isCheckPhoneUninitialized && update("getCheckPhoneNumber", draft => null)
    resetAddLead()
  }

  const handleAddLead = async leadValues => {
    let clientAgainstLead = client ? client : clientFormValues
    try {
      await addLead({ client: clientAgainstLead, ...leadValues }).unwrap()
      resetForm()
      fireEvent({
        ...ACTION_TYPES.add_new_client_submit,
        client_type: !!phoneData?.exists ? "existing" : "new",
        realocated: !!changeAssigneeData ? "yes" : "no",
        property_type: leadValues?.propertyType,
        property_sub_type: leadValues?.subType?.name,
        response: "200",
        project_name: leadValues?.project?.name,
      })
    } catch (e) {
      // notify.error(e.message)
      fireEvent({
        ...ACTION_TYPES.add_new_client_submit,
        client_type: !!phoneData?.exists ? "existing" : "new",
        realocated: !!changeAssigneeData ? "yes" : "no",
        property_type: leadValues?.propertyType,
        property_sub_type: leadValues?.subType?.name,
        response: e.status,
        project_name: leadValues?.project?.name,
      })
    }
  }

  const renderAllocationButton = () => {
    if (phoneData?.data) {
      // data?.Client?.User?.id !== user.id && getPrivilege("can_change_assignee", privileges)
      return phoneData?.isAllocationPossible ? (
        <>
          <SelectInput
            style={{ minWidth: 200 }}
            placeholder="Select Reason"
            options={reasons}
            loading={fetchingReasons}
            getPopupContainer={false}
            allowClear
            onChange={value => {
              setReason(value)
            }}
          />
        </>
      ) : null
    }
  }

  const renderOkText = () => {
    if (stage === 0 && phoneData?.isAllocationPossible) {
      return t("Reallocate")
    } else if (stage === 2) {
      return t("Add Lead")
    } else {
      return t("Next")
    }
  }

  const renderAffiliateSection = () => {
    return (
      <>
        {!!isAfilliateUser ? (
          !!phoneData?.data &&
          phoneData?.isAllocationPossible && (
            <>
              <Legend title={t("Affiliate Details")} />
              <Group template="repeat(2, 1fr)" gap="16px 64px" className="mbe-24">
                <AffiliateSection rules={rules} form={initializeClientForm} subModuleName="editClient" />
              </Group>
            </>
          )
        ) : (
          <></>
        )}
      </>
    )
  }

  const renderAllocationMsg = () => {
    if (errorState) {
      return (
        <IconText
          icon="MdInfoOutline"
          iconProps={{ color: theme["error-color"], size: "1em" }}
          textType="danger"
          title={errorState}
        />
      )
    }
    if (phoneData?.isUserCellNumber) {
      return (
        <IconText
          icon="MdInfoOutline"
          iconProps={{ color: theme["error-color"], size: "1em" }}
          textType="danger"
          title={t("This cell no belongs to a staff user")}
        />
      )
    }
    if (phoneData?.data) {
      const data = phoneData?.clientReallocationStatus?.data
      return data?.userId === user?.id ? (
        <IconText
          icon="MdInfoOutline"
          iconProps={{ color: theme["success-color"], size: "1em" }}
          textType="success"
          title={t("Client is already allocated to you.")}
        />
      ) : phoneData?.isAllocationPossible ? (
        <IconText
          icon="MdInfoOutline"
          iconProps={{ color: theme["success-color"], size: "1em" }}
          textType="success"
          title={t("The client exists and can be re-allocated to you. Please click on the Re-Allocate Client button.")}
        />
      ) : phoneData?.source === "personal" ? (
        <IconText
          icon="MdInfoOutline"
          iconProps={{ color: theme["error-color"], size: "1em" }}
          textType="danger"
          title={t("The client source is personal. Kindly request BSE team for manual reallocation of client.")}
        />
      ) : (
        <IconText
          icon="MdInfoOutline"
          iconProps={{ color: theme["error-color"], size: "1em" }}
          textType="danger"
          title={`${t("The client is active with")} ${data?.userName} - ${data?.userContactNumber} ${t("of")} ${
            data?.teamName
          } ${t("from")} ${data?.teamAddress}.\n ${t("Please contact him/her to share the client with you")}.`}
        />
      )
    }
  }

  const titleWithStageNavigation = () => {
    return (
      <Flex align="center" gap={4}>
        {stage > 0 && (
          <Button
            type="text"
            icon={<Icon icon={"IoIosArrowBack"} size="22px" color="#9d9d9d" />}
            onClick={() => {
              handleStageChange("backward")
            }}
            style={{ marginInlineStart: -12 }}
          />
        )}
        <div>{stage === 2 ? t("Add Lead") : t("Add Client")}</div>
      </Flex>
    )
  }

  return (
    <Modal
      title={titleWithStageNavigation()}
      ref={ref}
      width={900}
      onCancel={() => {
        resetForm()
      }}
      onOk={() => {
        stage === 0 && phoneData?.isAllocationPossible ? onReallocate() : handleStageChange("forward")
      }}
      okText={renderOkText()}
      disableOkButton={stage === 0 ? disableButton : false}
      hideCancelButton={true}
      onCancelProps={{
        onClick: () => {
          handleStageChange("backward")
        },
        cancelText: stage > 0 ? "Previous" : "Cancel",
      }}
      onModalVisible={onModalVisible}
      destroyOnClose
      footerAlert={error}
      loading={isAddLeadLoading || isChangeAssigneeLoading || isClientFetching}
    >
      <Form
        onKeyDown={e => {
          if (disableButton && e.key === "Enter") {
            e.preventDefault()
          }
        }}
        form={initializeClientForm}
        layout="vertical"
        name="client-form-initialize"
        initialValues={{ mobileNumber: "", searchAffiliate: affiliateOptions()?.[0] }}
        onFinish={async () => {
          const clientId = phoneData?.data?.Client?.id
          const nextStage = phoneData?.data?.Client?.User.id === user.id ? 2 : 1
          nextStage === 2 && getClientDetail(clientId)
          setStage(nextStage)
        }}
        scrollToFirstError
        preserve={true}
        hidden={stage !== 0}
      >
        <Legend title={t("Client Details")} />
        <Row style={{ gap: 8 }} align="top">
          <Form.Item
            label={t("Mobile Number")}
            required
            hasFeedback
            validateTrigger={["onBlur"]}
            name="mobileNumber"
            rules={rules}
            extra={renderAllocationMsg()}
            style={{ flexBasis: "48ch" }}
          >
            <PhoneInput
              onChange={async input => {
                initializeClientForm.setFields([
                  {
                    name: "mobileNumber",
                    errors: [],
                  },
                ])
                if (input) {
                  setErrorState(null)
                  await checkPhone({ cell: input.split(" ").join(""), reAllocationCheck: true })
                    .unwrap()
                    .catch(error => setErrorState(error?.message))
                }
              }}
            />
          </Form.Item>

          <Row style={{ gap: 8, marginTop: 30, height: 46 }} align="middle">
            {phoneValidationFetching ? (
              <>
                <Spinner type="plain" />
                <Text type="success">Validating this number</Text>
              </>
            ) : (
              !isPhoneError && renderAllocationButton()
            )}
          </Row>
        </Row>
        {renderAffiliateSection()}
      </Form>

      {stage === 1 && (
        <AddClientForm
          ref={addClientFormRef}
          preserve={true}
          onSubmit={values => {
            setClientFormValues(values)
            setStage(2)
          }}
        />
      )}

      {stage === 2 && (client || clientFormValues) && (
        <AddLeadForm
          ref={addLeadFormRef}
          clientFormValues={clientFormValues}
          client={client}
          onSubmit={handleAddLead}
          preserve={true}
        />
      )}
    </Modal>
  )
})
//
export default ClientLeadForm
